import {
  Centrifuge,
  ErrorContext,
  PublicationContext,
  SubscriptionErrorContext,
} from "centrifuge";
import { Observable } from "rxjs";
import { first, switchMap } from "rxjs/operators";
import { fetcher } from "./fetcher";
import canopyUrls from "canopy-urls!sofe";
import auth from "cp-client-auth!sofe";

const getCentrifugeUrl = () => {
  const env = canopyUrls.getEnvironment();
  if (env === canopyUrls.PRODUCTION_ENVIRONMENT) {
    return "wss://socket.canopytax.com/connection/websocket";
  }
  if (env === canopyUrls.STAGING_ENVIRONMENT) {
    return "wss://socket-stage.canopy.dev/connection/websocket";
  }
  if (env === canopyUrls.DEVELOPMENT_ENVIRONMENT) {
    return "wss://socket-local.canopy.dev:9555/connection/websocket";
  }
  return "wss://socket-integ.canopy.dev/connection/websocket";
};

// Max listeners before we get MaxListenersExceededWarning warnings
// That warning is to help indicate a _potential_ memory leak but we are cleaning up subscriptions properly it's just that some pages have a lot of listeners on the server side channels.
// 30 seems like a good amount that should be rare to exceed and in which case a warning would be justified to investigate further.
const MAX_LISTENERS = 30;

let centrifugeClient: Centrifuge;

type LoggedInUser = { id: string; tenant_id: string };

/* Server-side subscriptions are subscribed to when the client is connected and it broadcasts messages globally on the client.
 * The channels that use server-side subscriptions are determined by BE and right now it's only the user's private channel and tenant channel.
 * If this ever changes we should have BE return a list of server-side channels possibly from the wg/centrifugo-authenticate endpoint.
 * ^^ This endpoint would be necessary since we can't reliably determine the server-side channels. getSubscription does not return for server-side subs and client._serverSubs does not always populate immediately after connection.
 */
const isServerSideSubscription = (
  channel: string,
  loggedInUser: LoggedInUser
) => {
  return (
    channel === `private:${loggedInUser.id}` ||
    channel === `private:tenant:${loggedInUser.tenant_id}`
  );
};

const authenticateCentrifugo = (): Promise<string> => {
  return fetcher<{ token: string }>(`/wg/centrifugo-authenticate`).then(
    (data) => data.token
  );
};

const initializeCentrifuge = () => {
  centrifugeClient = new Centrifuge(getCentrifugeUrl(), {
    getToken: authenticateCentrifugo,
    debug: canopyUrls.getEnvironment() !== canopyUrls.PRODUCTION_ENVIRONMENT, // Enable debug in non-production for now
  });

  // @ts-expect-error setMaxListeners does exist on the centrifuge client
  centrifugeClient.setMaxListeners(MAX_LISTENERS);
  centrifugeClient.on("error", (ctx: ErrorContext) => {
    console.error("Centrifuge client error:", ctx.error);
  });

  centrifugeClient.connect();
};

// Ensure Centrifuge client is initialized and connected
const getCentrifugeClient = () => {
  if (!centrifugeClient) {
    initializeCentrifuge();
  } else if (
    centrifugeClient.state !== "connected" &&
    centrifugeClient.state !== "connecting"
  ) {
    centrifugeClient.connect();
  }
  return centrifugeClient;
};

// Handle server-side subscriptions by listening on the client directly
function subscribeToServerSideChannel(
  client: Centrifuge,
  channelName: string,
  eventName: string
): Observable<unknown> {
  return new Observable((observer) => {
    const publicationHandler = (ctx: PublicationContext) => {
      // Filter by channel name since all server-side subs come through this event
      if (
        ctx.channel === channelName &&
        ctx.data &&
        ctx.data.event === eventName
      ) {
        observer.next(ctx.data.message);
      }
    };

    client.on("publication", publicationHandler);
    return () => {
      client.off("publication", publicationHandler);
    };
  });
}

// Client side subscriptions use Centrifugo's Subscription API to manage subscriptions.
// Only one sub should exist per channel but it can have multiple listeners.
const channelSubscriberCounts: Record<string, number> = {};
function subscribeToClientSideChannel(
  client: Centrifuge,
  channelName: string,
  eventName: string
): Observable<unknown> {
  let subscription = client.getSubscription(channelName);
  if (!subscription) {
    subscription = client.newSubscription(channelName);
    // @ts-expect-error setMaxListeners does exist on the subscription
    subscription.setMaxListeners(MAX_LISTENERS);
    subscription.subscribe();
    channelSubscriberCounts[channelName] = 0;
  }
  channelSubscriberCounts[channelName]++;

  return new Observable((observer) => {
    const publicationHandler = (ctx: PublicationContext) => {
      if (ctx.data && ctx.data.event === eventName) {
        observer.next(ctx.data.message);
      }
    };
    const errorHandler = (err: SubscriptionErrorContext) => {
      console.error("Centrifuge subscription error:", err);
    };

    subscription.on("publication", publicationHandler);
    subscription.on("error", errorHandler);

    return () => {
      subscription.off("publication", publicationHandler);
      subscription.off("error", errorHandler);

      channelSubscriberCounts[channelName]--;
      if (channelSubscriberCounts[channelName] === 0) {
        subscription.unsubscribe();
        client.removeSubscription(subscription);
        delete channelSubscriberCounts[channelName];
      }
    };
  });
}

export function onCentrifuge(eventName: string, channelName?: string) {
  return auth.getLoggedInUserAsObservable().pipe(
    first(),
    switchMap((loggedInUser: LoggedInUser) => {
      const resolvedChannel = channelName ?? `private:${loggedInUser.id}`;
      const client = getCentrifugeClient();

      if (isServerSideSubscription(resolvedChannel, loggedInUser)) {
        return subscribeToServerSideChannel(client, resolvedChannel, eventName);
      } else {
        return subscribeToClientSideChannel(client, resolvedChannel, eventName);
      }
    })
  );
}
